import type { Flashcard, FlashcardCollection, FlashcardDeck, FlashcardNote, FlashcardReview } from "@japa-reader/contracts";

export function searchFlashcards(collection: FlashcardCollection, query: string, now = Date.now()): Flashcard[] {
  const normalized = query.trim();
  if (!normalized) return [...collection.cards];
  const notes = new Map(collection.notes.map((note) => [note.id, note]));
  const decks = new Map(collection.decks.map((deck) => [deck.id, deck]));
  const reviewsByCard = groupReviews(collection.reviews);
  const groups = splitOrGroups(tokenize(normalized));
  return collection.cards.filter((card) => groups.some((group) => group.every((token) => {
    const negated = token.startsWith("-");
    const term = negated ? token.slice(1) : token;
    const matched = matchTerm(card, notes.get(card.noteId), decks.get(card.deckId), reviewsByCard.get(card.id) || [], term, now, collection);
    return negated ? !matched : matched;
  })));
}

function matchTerm(
  card: Flashcard,
  note: FlashcardNote | undefined,
  deck: FlashcardDeck | undefined,
  reviews: FlashcardReview[],
  rawTerm: string,
  now: number,
  collection: FlashcardCollection,
) {
  const term = unquote(rawTerm);
  const separator = term.indexOf(":");
  const operator = separator > 0 ? term.slice(0, separator).toLowerCase() : "";
  const value = separator > 0 ? unquote(term.slice(separator + 1)) : term;
  if (operator === "deck") return deckMatches(deck, value, collection.decks);
  if (operator === "tag") return note?.tags.some((tag) => wildcard(tag, value)) ?? false;
  if (operator === "is") return stateMatches(card, value.toLowerCase(), now);
  if (operator === "flag") return card.flag === Number(value);
  if (operator === "card") return wildcard(card.templateId, value) || String(card.templateOrdinal + 1) === value;
  if (operator === "note") {
    const type = collection.noteTypes.find((candidate) => candidate.id === note?.noteTypeId);
    return Boolean(type && (wildcard(type.name, value) || wildcard(type.id, value)));
  }
  if (operator === "nid") return wildcard(card.noteId, value);
  if (operator === "cid") return wildcard(card.id, value);
  if (operator === "added") return withinDays(note?.createdAt, value, now);
  if (operator === "edited") return withinDays(note?.updatedAt, value, now);
  if (operator === "rated") return ratedMatches(reviews, value, now);
  if (operator === "prop") return propertyMatches(card, value, now);
  if (operator && note && Object.keys(note.fields).some((field) => field.toLowerCase() === operator)) {
    const field = Object.entries(note.fields).find(([name]) => name.toLowerCase() === operator)?.[1] || "";
    return wildcard(stripHtml(field), value);
  }
  const haystack = [
    ...Object.values(note?.fields || {}).map(stripHtml),
    ...(note?.tags || []),
    deck?.name || "",
  ].join(" ");
  return wildcard(haystack, term);
}

function stateMatches(card: Flashcard, value: string, now: number) {
  if (value === "new") return card.state === "new";
  if (value === "learn" || value === "learning") return card.state === "learning" || card.state === "relearning";
  if (value === "review") return card.state === "review";
  if (value === "due") return !card.suspended && !isBuried(card, now) && card.due <= now;
  if (value === "suspended") return card.suspended;
  if (value === "buried") return isBuried(card, now);
  return false;
}

function propertyMatches(card: Flashcard, value: string, now: number) {
  const match = value.match(/^(due|ivl|reps|lapses|ease|stability|difficulty)(<=|>=|!=|=|<|>)(-?\d+(?:\.\d+)?)$/i);
  if (!match) return false;
  const [, property, comparison, rawExpected] = match;
  const expected = Number(rawExpected);
  const actual = property.toLowerCase() === "due"
    ? Math.ceil((card.due - startOfDay(now)) / 86_400_000)
    : property.toLowerCase() === "ivl" ? card.scheduledDays
      : property.toLowerCase() === "ease" ? card.difficulty
        : card[property.toLowerCase() as "reps" | "lapses" | "stability" | "difficulty"];
  if (comparison === "<=") return actual <= expected;
  if (comparison === ">=") return actual >= expected;
  if (comparison === "!=") return actual !== expected;
  if (comparison === "<") return actual < expected;
  if (comparison === ">") return actual > expected;
  return actual === expected;
}

function ratedMatches(reviews: FlashcardReview[], value: string, now: number) {
  const [rawDays, rawRating] = value.split(":");
  const days = Number(rawDays);
  const rating = rawRating ? Number(rawRating) : null;
  if (!Number.isFinite(days)) return false;
  const since = startOfDay(now) - Math.max(0, days - 1) * 86_400_000;
  return reviews.some((review) => review.reviewedAt >= since && (rating === null || review.rating === rating));
}

function deckMatches(deck: FlashcardDeck | undefined, value: string, decks: FlashcardDeck[]) {
  if (!deck) return false;
  const path = deckPath(deck, decks);
  return wildcard(path, value) || wildcard(deck.name, value);
}

export function deckPath(deck: FlashcardDeck, decks: FlashcardDeck[]) {
  const names = [deck.name];
  const byId = new Map(decks.map((candidate) => [candidate.id, candidate]));
  const seen = new Set([deck.id]);
  let current = deck;
  while (current.parentId) {
    const parent = byId.get(current.parentId);
    if (!parent || seen.has(parent.id)) break;
    names.unshift(parent.name);
    seen.add(parent.id);
    current = parent;
  }
  return names.join("::");
}

function withinDays(timestamp: number | undefined, value: string, now: number) {
  const days = Number(value);
  return Boolean(timestamp && Number.isFinite(days) && timestamp >= startOfDay(now) - Math.max(0, days - 1) * 86_400_000);
}

function groupReviews(reviews: FlashcardReview[]) {
  const grouped = new Map<string, FlashcardReview[]>();
  for (const review of reviews) grouped.set(review.cardId, [...(grouped.get(review.cardId) || []), review]);
  return grouped;
}

function splitOrGroups(tokens: string[]) {
  const groups: string[][] = [[]];
  for (const token of tokens) {
    if (token.toUpperCase() === "OR") groups.push([]);
    else groups.at(-1)?.push(token);
  }
  return groups.filter((group) => group.length);
}

function tokenize(query: string) {
  return query.match(/(?:[^\s"]+|"[^"]*")+/g) || [];
}

function wildcard(value: string, pattern: string) {
  const normalizedValue = normalize(value);
  const normalizedPattern = normalize(pattern);
  if (!normalizedPattern.includes("*")) return normalizedValue.includes(normalizedPattern);
  const expression = normalizedPattern.split("*").map(escapeRegex).join(".*");
  return new RegExp(expression, "i").test(normalizedValue);
}

function normalize(value: string) {
  return value.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function escapeRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function unquote(value: string) {
  return value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1) : value;
}

function stripHtml(value: string) {
  if (typeof DOMParser !== "undefined") return new DOMParser().parseFromString(value, "text/html").body.textContent || "";
  return value.replace(/<[^>]+>/g, " ");
}

function isBuried(card: Flashcard, now: number) {
  return card.buriedUntil !== null && card.buriedUntil > now;
}

function startOfDay(timestamp: number) {
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}
